/**
 * <ImmersionPlayer /> — RLI 3-Layer Subliminal Architecture (Reclaim spec).
 *
 * Principle: state + imprinting, not content delivery.
 * The user must feel calm and unaware of structure — absorption, not instruction.
 *
 *   Layer 1 — Base Immersion (consciously felt)
 *     Slow cinematic forward-motion over the master horizon scene.
 *     Soft blur + cool tone + low contrast center. Universal across journeys.
 *
 *   Layer 2 — Subliminal Visual Imprints (peripherally felt)
 *     50–150ms keyword/symbol flashes at screen edges, in irregular waves.
 *     Never centered. Never readable as instruction.
 *
 *   Layer 3 — Whisper Audio (sub-perceptual)
 *     Low-volume, low-pass-filtered whisper layer beneath ambient music.
 *     OFF / LIGHT / DEEP — user toggle (DEEP defaults at night).
 *
 * The central `words` (I see, steady, clear) remain — those are the conscious
 * touchpoints, NOT subliminals. They live in the calm center; subliminals
 * live at the edges.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";
import useSilentModeUnlock from "../../hooks/useSilentModeUnlock";
import SignatureTrackCredit from "../SignatureTrackCredit";

const API = process.env.REACT_APP_BACKEND_URL;

// Master immersion canvas — universal RLI signature.
// Coastal path / cliffside walking forward into ocean horizon.
// Slow cinematic forward motion (CSS scale) over the calm scene.
const MASTER_BASE_IMAGE = `${API}/api/journey-images/uk-driving-theory/rli_master_coastal.png`;
const MASTER_BASE_FALLBACK = `${API}/api/journey-images/uk-driving-theory/ambient_horizon.png`;

// Per-journey cinematic VIDEO base layer. When present, replaces the still
// image layer 1 (forward-motion) with a real cinematic loop. The RLI
// subliminal layers (peripheral flashes, vignette, gradients) still render
// on top exactly as before.
//
// Slug → array of { src, type } sources. Browser picks the first it can
// decode. We always ship BOTH H.264 MP4 (Safari + licensed Chrome/Edge)
// and VP9 WebM (FOSS Chromium / Firefox / automated tests) so playback
// works everywhere.
const JOURNEY_BASE_VIDEOS = {
  "uk-driving-theory": [
    { src: `${API}/api/journey-videos/driving_immersion_v1.webm`, type: "video/webm" },
    { src: `${API}/api/journey-videos/driving_immersion_v1.mp4`, type: "video/mp4" },
  ],
};

// Subliminal mode persistence
const SUBLIMINAL_MODE_KEY = "reclaim_subliminal_mode";
const SUBLIMINAL_MODES = ["off", "light", "deep"];
const DEFAULT_MODE = "light";

// Edge positions for subliminal flashes — 80% peripheral, 20% near-centre.
// Per RLI spec: keep awareness open, never cognitive overload.
const EDGE_SLOTS = [
  // 80% peripheral edge slots
  { top: "8%",  left: "8%",  zone: "edge" },
  { top: "12%", left: "82%", zone: "edge" },
  { top: "22%", left: "4%",  zone: "edge" },
  { top: "28%", left: "88%", zone: "edge" },
  { top: "68%", left: "10%", zone: "edge" },
  { top: "74%", left: "78%", zone: "edge" },
  { top: "82%", left: "30%", zone: "edge" },
  { top: "18%", left: "62%", zone: "edge" },
  // 20% near-centre slots (rare, very soft)
  { top: "38%", left: "30%", zone: "near" },
  { top: "58%", left: "60%", zone: "near" },
];

/* ────────────────────────────────────────────────────────────────────
 *  Layer 2 — RLI 3-minute Subliminal Arc
 *
 *  Arrival (0:00–0:30)         🟢  No flashes (or one ultra-soft seed)
 *  Opening (0:30–1:30)         🟡  Burst every 8–12s, 1–2 flashes, keywords
 *  Peak Imprint (1:30–2:30)    🔵  Burst every 5–7s, 2–4 flashes, mix of
 *                                  60% keywords / 25% phrases / 15% symbols
 *  Integration (2:30–3:00)     🟣  Burst every 10–15s, 1 flash, familiar only
 *
 *  Looping: timing/order/position are randomised so the brain perceives
 *  freshness, never a metronome.
 * ────────────────────────────────────────────────────────────────── */
function planSubliminalArc(keywords, phrases, totalSeconds) {
  if ((!keywords || keywords.length === 0) && (!phrases || phrases.length === 0)) {
    return [];
  }
  const events = [];
  const rand = (a, b) => a + Math.random() * (b - a);
  const randInt = (a, b) => Math.floor(rand(a, b + 1));
  const pickKeyword = (() => {
    let i = Math.floor(Math.random() * (keywords.length || 1));
    return () => {
      i = (i + 1 + randInt(0, 1)) % (keywords.length || 1);
      return keywords[i] || phrases[0] || "calm";
    };
  })();
  const pickPhrase = (() => {
    let i = 0;
    return () => {
      const list = phrases.length ? phrases : keywords;
      i = (i + 1) % list.length;
      return list[i];
    };
  })();
  const pickSlot = (() => {
    let i = Math.floor(Math.random() * EDGE_SLOTS.length);
    return (preferEdge = true) => {
      // 80% edge, 20% near
      const wantEdge = preferEdge && Math.random() < 0.8;
      const pool = EDGE_SLOTS.filter((s) => (wantEdge ? s.zone === "edge" : true));
      i = (i + 1 + randInt(0, 2)) % pool.length;
      return pool[i];
    };
  })();
  const pushFlash = (t, content, opacityMul = 1, holdRange = [70, 130]) => {
    if (t >= totalSeconds - 4) return;
    events.push({
      appearAt: Number(t.toFixed(2)),
      holdMs: Math.round(rand(holdRange[0], holdRange[1])),
      text: content,
      slot: pickSlot(true),
      opacityMul,
    });
  };

  // ───── Phase 1 — Arrival (0:00–0:30): one early seed + one mid-seed
  // Originally only at 22s — too late on Day 1 testing. Bring forward
  // so users perceive the layer is alive within the first 10s.
  if (keywords.length) {
    pushFlash(8 + rand(-1, 1), pickKeyword(), 0.55, [70, 110]);
    pushFlash(20 + rand(-2, 2), pickKeyword(), 0.50, [60, 95]);
  }

  // ───── Phase 2 — Opening (0:30–1:30): light seeding ────────────
  let t = 30 + rand(2, 6); // first opening burst at ~32–36s
  while (t < 90) {
    const burstSize = Math.random() < 0.55 ? 1 : 2;
    for (let i = 0; i < burstSize; i++) {
      pushFlash(t + i * rand(0.4, 0.9), pickKeyword(), 0.85, [80, 120]);
    }
    t += rand(8, 12);
  }

  // ───── Phase 3 — Peak Imprint (1:30–2:30): engine ──────────────
  t = 90 + rand(0, 2);
  while (t < 150) {
    const burstSize = randInt(2, 4);
    for (let i = 0; i < burstSize; i++) {
      // Content mix: 60% keywords / 25% phrases / 15% symbolic-like
      const r = Math.random();
      let content;
      let holdRange = [50, 100];
      if (r < 0.6) {
        content = pickKeyword();
      } else if (r < 0.85 && phrases.length) {
        content = pickPhrase();
      } else {
        // "Symbolic" — short evocative single word from keywords pool,
        // placed slightly larger / softer (kept textual since we don't
        // have authored symbol assets yet)
        content = `· ${pickKeyword()} ·`;
        holdRange = [70, 110];
      }
      pushFlash(t + i * rand(0.35, 0.85), content, 1.0, holdRange);
    }
    t += rand(5, 7);
  }

  // ───── Phase 4 — Integration (2:30–3:00): taper ───────────────
  t = 150 + rand(0, 3);
  // Use familiar items (first 5 keywords seen) to lock in
  const familiar = keywords.slice(0, 5);
  let famIdx = 0;
  while (t < totalSeconds - 4) {
    const word = familiar[famIdx % (familiar.length || 1)] || pickKeyword();
    famIdx += 1;
    pushFlash(t, word, 0.7, [80, 120]);
    t += rand(10, 15);
  }

  // Sort by appearAt to keep the runtime scheduler simple
  events.sort((a, b) => a.appearAt - b.appearAt);
  return events;
}

/* ────────────────────────────────────────────────────────────────────
 *  Mode toggle pill — OFF / LIGHT / DEEP
 * ────────────────────────────────────────────────────────────────── */
function SubliminalModeToggle({ mode, onChange }) {
  return (
    <div
      className="flex items-center rounded-full px-1 py-1"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.10)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      data-testid="subliminal-mode-toggle"
    >
      {SUBLIMINAL_MODES.map((m) => {
        const active = m === mode;
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            className="px-3 py-1.5 rounded-full text-[9.5px] tracking-[0.32em] uppercase transition-all"
            style={{
              background: active
                ? "linear-gradient(135deg, rgba(77,163,255,0.28) 0%, rgba(46,123,214,0.20) 100%)"
                : "transparent",
              color: active ? "#FFFFFF" : "rgba(255,255,255,0.55)",
              fontFamily: JOURNEY_FONT_LABEL,
              fontWeight: 500,
              border: active
                ? "1px solid rgba(77,163,255,0.45)"
                : "1px solid transparent",
            }}
            data-testid={`subliminal-mode-${m}`}
          >
            {m}
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  Main component
 * ────────────────────────────────────────────────────────────────── */
export default function ImmersionPlayer({
  durationSeconds = 180,         // RLI master arc = 3 minutes
  words = [],
  subliminalKeywords = [],       // Layer 2 — single keywords (60% of mix)
  subliminalPhrases = [],        // Layer 2 — micro phrases (25% of mix)
  subliminalAudioUrl = null,     // Layer 3 — optional whisper track URL
  visual = null,                 // (kept for back-compat; unused with master canvas)
  musicArea = "journey_immersion",
  musicMode = "soft",
  slug = null,                   // Journey slug → picks per-journey base video if mapped
  onComplete,
}) {
  const [elapsed, setElapsed] = useState(0);
  const [activeWord, setActiveWord] = useState(null);
  const [musicReady, setMusicReady] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [activeFlash, setActiveFlash] = useState(null);
  const [baseImgFailed, setBaseImgFailed] = useState(false);
  const [baseVideoFailed, setBaseVideoFailed] = useState(false);
  const [subliminalMode, setSubliminalMode] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_MODE;
    return localStorage.getItem(SUBLIMINAL_MODE_KEY) || DEFAULT_MODE;
  });

  const baseVideoSources = slug ? JOURNEY_BASE_VIDEOS[slug] : null;
  const useVideoBase = !!baseVideoSources && baseVideoSources.length > 0 && !baseVideoFailed;

  const musicRef = useRef(null);
  const whisperRef = useRef(null);
  const musicFadeRafRef = useRef(null);
  const { unlock, lock } = useSilentModeUnlock();
  // Force iOS into media-playback session so the ambient music
  // remains audible with the ringer switch off.
  useEffect(() => {
    unlock();
    return () => lock();
  }, [unlock, lock]);
  // Track name surfaced briefly in the lower-right corner as a
  // "Reclaim Original" credit. Populated by the music resolve.
  const [trackName, setTrackName] = useState(null);
  const wordIndexRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const wordTimeoutRef = useRef(null);
  const flashTimerRef = useRef(null);
  const flashIndexRef = useRef(0);

  // Refs that mirror state for use INSIDE the setInterval closure
  // (avoids stale-closure bug that caused the music to fade out
  // re-triggering every tick at the 180s boundary)
  const exitingRef = useRef(false);
  const subliminalModeRef = useRef(subliminalMode);
  useEffect(() => { subliminalModeRef.current = subliminalMode; }, [subliminalMode]);

  // Persist mode + adjust live whisper volume on change
  useEffect(() => {
    try {
      localStorage.setItem(SUBLIMINAL_MODE_KEY, subliminalMode);
    } catch {}
    if (whisperRef.current) {
      whisperRef.current.volume = whisperVolumeFor(subliminalMode, elapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subliminalMode]);

  // Flash plan — derive once per (keywords/phrases, duration) tuple.
  // We stringify the keyword/phrase arrays so that re-renders that pass
  // referentially-new but value-equal arrays don't tear down the interval.
  const flashPlanKey = useMemo(
    () => `${durationSeconds}|${(subliminalKeywords || []).join(",")}|${(subliminalPhrases || []).join(",")}`,
    [subliminalKeywords, subliminalPhrases, durationSeconds]
  );
  const flashPlan = useMemo(
    () => planSubliminalArc(subliminalKeywords, subliminalPhrases, durationSeconds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flashPlanKey]
  );
  const flashPlanRef = useRef(flashPlan);
  useEffect(() => { flashPlanRef.current = flashPlan; }, [flashPlan]);

  // Pre-load base image; if it 404s, fall back to road horizon
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBaseImgFailed(false);
    img.onerror = () => setBaseImgFailed(true);
    img.src = MASTER_BASE_IMAGE;
  }, []);
  const baseUrl = baseImgFailed ? MASTER_BASE_FALLBACK : MASTER_BASE_IMAGE;

  // ─── Layer 3: whisper audio loop (only when LIGHT/DEEP + URL given) ──
  useEffect(() => {
    if (!subliminalAudioUrl) return;
    const audio = new Audio(subliminalAudioUrl);
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = whisperVolumeFor(subliminalMode, 0);
    whisperRef.current = audio;
    audio.play().catch(() => {
      /* autoplay blocked — fine, whisper silently absent */
    });
    return () => {
      try {
        audio.pause();
        audio.src = "";
      } catch {}
      whisperRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subliminalAudioUrl]);

  // ─── Layer 1 audio: ambient music (existing unified-audio resolve) ──
  useEffect(() => {
    let cancelled = false;
    const warm = async () => {
      try {
        const r = await fetch(
          `${API}/api/unified-audio/resolve?area=${encodeURIComponent(
            musicArea
          )}&mode=${encodeURIComponent(musicMode)}`
        );
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const u = data?.music?.url;
        const url = u ? (u.startsWith("http") ? u : `${API}${u}`) : null;
        if (!url) return;
        // Surface the track name as a "Reclaim Original" credit.
        const displayName = data?.music?.name
          || data?.music?.id?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
          || null;
        if (displayName) setTrackName(displayName);
        const audio = new Audio(url);
        audio.loop = true;
        audio.volume = 0;
        audio.preload = "auto";
        // NOTE: crossOrigin intentionally NOT set — the server is
        // same-origin and doesn't emit CORS headers, so setting
        // crossOrigin:"anonymous" makes iOS silently fail audio decode.
        musicRef.current = audio;

        // Safety net: if audio gets paused unexpectedly while we're not
        // exiting (browser tab backgrounded → resumed, iOS audio session
        // interruption, etc.), gently resume from where we are.
        const onUnexpectedPause = () => {
          if (cancelled || exitingRef.current) return;
          if (audio.ended) {
            try { audio.currentTime = 0; } catch {}
          }
          audio.play().catch(() => {});
        };
        audio.addEventListener("pause", onUnexpectedPause);
        // Some browsers don't honour audio.loop on every MP3 — listen for
        // 'ended' as a backup and seek to 0.
        const onEnded = () => {
          if (cancelled || exitingRef.current) return;
          try { audio.currentTime = 0; } catch {}
          audio.play().catch(() => {});
        };
        audio.addEventListener("ended", onEnded);

        // Fire-and-forget play. On iOS Safari, audio.play()'s promise
        // can hang unresolved for many seconds while playback already
        // started — so we kick off the volume fade on its own clock
        // (not chained to .then()) and use the play promise only for
        // its rejection signal. This is the fix for "video plays but
        // no music ever comes on" reported by the user.
        const playPromise = audio.play();
        const target = data?.music?.music_volume ?? data?.music_volume ?? data?.music?.volume ?? 0.45;
        const start = Date.now();
        let fadeRaf = null;
        const fade = () => {
          if (!musicRef.current || cancelled) return;
          const t = Math.min(1, (Date.now() - start) / 4000);
          try { musicRef.current.volume = target * t; } catch {}
          if (t < 1) fadeRaf = requestAnimationFrame(fade);
        };
        fadeRaf = requestAnimationFrame(fade);
        setMusicReady(true);
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => setMusicReady(false));
        }
        // Track the raf so cleanup can cancel it.
        musicFadeRafRef.current = fadeRaf;
      } catch {
        /* visual + words still run */
      }
    };
    warm();
    return () => {
      cancelled = true;
      if (musicFadeRafRef.current) {
        cancelAnimationFrame(musicFadeRafRef.current);
        musicFadeRafRef.current = null;
      }
      if (musicRef.current) {
        try {
          musicRef.current.pause();
          musicRef.current.src = "";
        } catch {}
        musicRef.current = null;
      }
      if (wordTimeoutRef.current) clearTimeout(wordTimeoutRef.current);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Tick: words + subliminal flashes + completion ──────────────────
  useEffect(() => {
    const id = setInterval(() => {
      // Guard: don't fire any logic while exiting (avoids re-triggering
      // the fade-out every tick — the original stale-closure bug).
      if (exitingRef.current) return;
      const e = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(e);

      // Conscious word scheduling (calm center)
      const next = words[wordIndexRef.current];
      if (next && e >= (next.appears_at_seconds || 0)) {
        setActiveWord(next);
        wordIndexRef.current += 1;
        const hold = (next.hold_seconds || 5) * 1000;
        if (wordTimeoutRef.current) clearTimeout(wordTimeoutRef.current);
        wordTimeoutRef.current = setTimeout(() => {
          setActiveWord((cur) => (cur === next ? null : cur));
        }, hold);
      }

      // Subliminal flash scheduling (Layer 2) — only when mode != "off"
      const mode = subliminalModeRef.current;
      const plan = flashPlanRef.current;
      if (mode !== "off") {
        const flash = plan[flashIndexRef.current];
        if (flash && e >= flash.appearAt) {
          flashIndexRef.current += 1;
          setActiveFlash(flash);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => {
            setActiveFlash((cur) => (cur === flash ? null : cur));
          }, flash.holdMs);
        }
      }

      // Whisper presence boost: per RLI spec, whisper layer is slightly
      // more present during the 1:30–2:30 peak imprint window.
      if (whisperRef.current) {
        whisperRef.current.volume = whisperVolumeFor(mode, e);
      }

      // Completion w/ 6s fade-out — runs exactly ONCE thanks to exitingRef
      if (e >= durationSeconds) {
        exitingRef.current = true;
        setExiting(true);
        if (musicRef.current) {
          const startVol = musicRef.current.volume;
          const start = Date.now();
          const fade = () => {
            if (!musicRef.current) return;
            const t = Math.min(1, (Date.now() - start) / 6000);
            musicRef.current.volume = startVol * (1 - t);
            if (t < 1) requestAnimationFrame(fade);
            else {
              try {
                musicRef.current.pause();
              } catch {}
            }
          };
          fade();
        }
        if (whisperRef.current) {
          const startVol = whisperRef.current.volume;
          const start = Date.now();
          const fade = () => {
            if (!whisperRef.current) return;
            const t = Math.min(1, (Date.now() - start) / 6000);
            whisperRef.current.volume = startVol * (1 - t);
            if (t < 1) requestAnimationFrame(fade);
            else {
              try {
                whisperRef.current.pause();
              } catch {}
            }
          };
          fade();
        }
        setTimeout(() => onComplete?.(), 6000);
      }
    }, 250);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndEarly = () => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setExiting(true);
    [musicRef.current, whisperRef.current].forEach((a) => {
      if (!a) return;
      const startVol = a.volume;
      const start = Date.now();
      const fade = () => {
        const t = Math.min(1, (Date.now() - start) / 2000);
        a.volume = startVol * (1 - t);
        if (t < 1) requestAnimationFrame(fade);
        else {
          try {
            a.pause();
          } catch {}
        }
      };
      fade();
    });
    setTimeout(() => onComplete?.(), 2000);
  };

  const remaining = Math.max(0, durationSeconds - elapsed);
  const mm = Math.floor(remaining / 60);
  const ss = (remaining % 60).toString().padStart(2, "0");

  return (
    <motion.div
      className="fixed inset-0 z-[80] overflow-hidden"
      data-testid="immersion-player"
      style={{ background: P.bgWash }}
      initial={{ opacity: 0 }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: exiting ? 6 : 2.5, ease: "easeInOut" }}
    >
      {/* ─── Layer 1: Master forward-motion canvas ─────────────────── */}
      {useVideoBase ? (
        <motion.video
          autoPlay
          muted
          playsInline
          loop
          preload="auto"
          // CSS scale on top of intrinsic motion = the same gentle
          // forward-zoom feel the still image had. Plays slightly
          // softened to match the RLI palette spec.
          //
          // NOTE: video starts at opacity 0.4 (not 0) so iOS Safari
          // doesn't deprioritize / skip rendering it. The remaining
          // fade is carried by the brightness filter ramping up
          // alongside the scale animation.
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.0, opacity: 0.4 }}
          animate={{ scale: 1.06, opacity: 0.92 }}
          transition={{
            opacity: { duration: 4.5, ease: "easeOut" },
            scale: { duration: durationSeconds, ease: "linear" },
          }}
          style={{
            // Per RLI spec: slightly soft, low-saturation warmth.
            filter: "saturate(0.82) brightness(0.86) blur(0.6px)",
          }}
          onError={() => setBaseVideoFailed(true)}
          data-testid="immersion-base-video"
        >
          {baseVideoSources.map((s, i) => (
            <source key={i} src={s.src} type={s.type} />
          ))}
        </motion.video>
      ) : (
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.0, opacity: 0 }}
          animate={{ scale: 1.18, opacity: 0.88 }}
          transition={{
            opacity: { duration: 4.5, ease: "easeOut" },
            scale: { duration: durationSeconds, ease: "linear" },
          }}
          style={{
            backgroundImage: `url(${baseUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center 55%",
            // Per RLI spec: slightly soft across whole frame, low-saturation
            // warmth, edges marginally softer than centre (vignette below).
            filter: "saturate(0.78) brightness(0.86) blur(1.2px)",
          }}
          data-testid="immersion-base-layer"
        />
      )}

      {/* Edge softness vignette — per spec: edges slightly more blurred */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 35%, rgba(0,0,0,0.22) 100%)",
          backdropFilter: "blur(0.6px)",
          WebkitBackdropFilter: "blur(0.6px)",
        }}
      />

      {/* Top + bottom dark gradient — per spec: 5-10% top, 20-30% bottom */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(13,27,42,0.30) 0%, rgba(13,27,42,0.0) 18%, rgba(13,27,42,0.0) 70%, rgba(13,27,42,0.55) 100%)",
        }}
      />

      {/* Subtle warm-gold horizon glow (5-8% warm filter per spec) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 40%, rgba(212,184,126,0.06) 58%, rgba(212,184,126,0.0) 78%)",
          mixBlendMode: "screen",
        }}
      />

      {/* ─── Layer 2: Subliminal peripheral flashes ──────────────────
          Per RLI spec: soft glow, slight blur, warm white / soft gold tone,
          no harsh contrast. Fade-in/out (not hard cut). */}
      <AnimatePresence>
        {activeFlash && !exiting && subliminalMode !== "off" && (
          <motion.span
            key={`${activeFlash.appearAt}-${activeFlash.text}`}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{
              opacity:
                (subliminalMode === "deep" ? 0.62 : 0.58) *
                (activeFlash.opacityMul || 1),
              scale: 1,
            }}
            exit={{ opacity: 0, scale: 1.04 }}
            transition={{
              opacity: { duration: 0.16, ease: "easeOut" },
              scale: { duration: 0.18, ease: "easeOut" },
            }}
            className="absolute pointer-events-none"
            style={{
              top: activeFlash.slot.top,
              left: activeFlash.slot.left,
              fontFamily: JOURNEY_FONT_BODY,
              fontWeight: 400,
              fontSize:
                activeFlash.slot.zone === "near"
                  ? "clamp(15px, 1.9vw, 21px)"
                  : "clamp(13px, 1.6vw, 17px)",
              letterSpacing: "0.22em",
              textTransform: "lowercase",
              // Warm white / soft gold tone per RLI spec
              color: "rgba(255,248,232,0.96)",
              textShadow:
                "0 0 18px rgba(228,200,142,0.55), 0 0 3px rgba(255,250,238,0.5)",
              filter: "blur(0.7px)",
            }}
            data-testid={`subliminal-flash-${activeFlash.text}`}
          >
            {activeFlash.text}
          </motion.span>
        )}
      </AnimatePresence>

      {/* ─── Conscious centre: cinematic affirmation word ──────────── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-6">
        <AnimatePresence>
          {activeWord && !exiting && (
            <motion.p
              key={activeWord.text + activeWord.appears_at_seconds}
              initial={{ opacity: 0, y: 14, scale: 0.96 }}
              animate={{ opacity: 0.92, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 1.02 }}
              transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
              className="text-center italic"
              style={{
                color: "#FFFFFF",
                fontFamily: JOURNEY_FONT_HEAD,
                fontSize: "clamp(28px, 6vw, 44px)",
                fontWeight: 300,
                letterSpacing: "0.01em",
                textShadow: "0 4px 28px rgba(77,163,255,0.45)",
                maxWidth: "20ch",
              }}
              data-testid={`immersion-word-${activeWord.text}`}
            >
              {activeWord.text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* ─── Bottom controls: timer + mode toggle + end-early ──────── */}
      <div className="absolute bottom-7 left-0 right-0 flex flex-col items-center gap-3">
        <SubliminalModeToggle
          mode={subliminalMode}
          onChange={(m) => setSubliminalMode(m)}
        />
        <p
          className="text-[10px] tracking-[0.4em] uppercase"
          style={{ color: "rgba(255,255,255,0.55)", fontFamily: JOURNEY_FONT_LABEL }}
          data-testid="immersion-remaining"
        >
          immersion · {mm}:{ss} remaining
        </p>
        <button
          onClick={handleEndEarly}
          className="text-[10px] tracking-[0.3em] uppercase opacity-50 hover:opacity-90 transition-opacity"
          style={{ color: "rgba(255,255,255,0.65)", fontFamily: JOURNEY_FONT_LABEL }}
          data-testid="immersion-complete-early-btn"
        >
          end early
        </button>
        {!musicReady && (
          <p
            className="text-[9px] tracking-[0.3em] uppercase opacity-40"
            style={{ color: "rgba(255,255,255,0.4)", fontFamily: JOURNEY_FONT_LABEL }}
          >
            visual only
          </p>
        )}
      </div>

      {/* Signature track credit — fades in ~1.2 s after immersion start, holds ~3 s, dissolves */}
      <SignatureTrackCredit name={trackName} show delay={1200} />
    </motion.div>
  );
}

/* Whisper volume map, now phase-aware per RLI spec.
 *   off     = silent
 *   light   = ~10–14% (peak boost during 1:30–2:30)
 *   deep    = ~18–24% (peak boost during 1:30–2:30)
 * The boost is gentle — still well below conscious awareness. */
function whisperVolumeFor(mode, elapsedSec = 0) {
  if (mode === "off") return 0;
  const inPeak = elapsedSec >= 90 && elapsedSec <= 150;
  if (mode === "deep") return inPeak ? 0.24 : 0.18;
  return inPeak ? 0.14 : 0.10;
}
